/* Tenancy + dirty-editor registry for the App Studio shell.
 * URL-derived org/app scoping, mirroring DataHubV2Context. */
import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef
} from 'react';
import {
    useParams
} from 'react-router-dom';
import type {
    Tenancy
} from './api/types';

interface AppStudioCtx {
    orgId: string;
    appId: string;
    tenancy: Tenancy;
    /** Mark/unmark an editor as holding unsaved changes. */
    setDirty: (key: string, dirty: boolean) => void;
    /** Ask the user before discarding unsaved changes. True = proceed. */
    confirmLeave: () => boolean;
}

const Ctx = createContext<AppStudioCtx | null>(null);

export function AppStudioProvider({
    children
}: {
    children: React.ReactNode;
}) {
    const params = useParams<any>();
    const orgId = params.oragID || params.orgId || '';
    const appId = params.appId || '';
    const dirtyRef = useRef<Set<string>>(new Set());

    const setDirty = useCallback((key: string, dirty: boolean) => {
        if (dirty) dirtyRef.current.add(key);
        else dirtyRef.current.delete(key);
    }, []);

    const confirmLeave = useCallback(() => {
        if (dirtyRef.current.size === 0) return true;
        const ok = window.confirm(
            'You have unsaved changes. Leave anyway and discard them?');
        if (ok) dirtyRef.current.clear();
        return ok;
    }, []);

    const value: AppStudioCtx = useMemo(() => ({
        orgId,
        appId,
        tenancy: {
            org_id: orgId,
            app_id: appId
        },
        setDirty,
        confirmLeave
    }), [orgId, appId, setDirty, confirmLeave]);

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAppStudio(): AppStudioCtx {
    const v = useContext(Ctx);
    if (!v) throw new Error('useAppStudio must be used inside <AppStudioProvider>');
    return v;
}
